import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { ChevronDownIcon } from '@radix-ui/react-icons';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import {
  IFaq,
  MainEntity,
  PageProps,
  microMarkupSchemaProps,
} from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Hero } from '@/components/common/Hero';
import { Heading } from '@/components/typography';
import { Section } from '@/components/common/Section';
import { FAQ_PAGE_QUERY } from '@/data/faqPageQuery';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { PageHead } from '@/components/common/PageHead';

const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);

  const { title, seo, faq } = data.pageBy.translation;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const mainEntity: MainEntity[] = faq?.section?.map(item => ({
    '@type': 'Question',
    name: item?.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item?.answer,
    },
  }));

  const microMarkupSchema: microMarkupSchemaProps = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        microMarkupSchema={microMarkupSchema}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Hero breadcrumbs={seo?.breadcrumbs}>
            <Heading tag="h1" withDivider content={title || ''} />
          </Hero>
          <Section className="py-15 md:py-25">
            <div>
              {faq.section.map((item: IFaq, index: number) => (
                <Accordion
                  defaultExpanded={index === 0}
                  key={index}
                  className="dark:bg-black"
                >
                  <AccordionSummary
                    expandIcon={<ChevronDownIcon width={24} height={24} />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                  >
                    <Typography
                      fontFamily={'inherit'}
                      className={
                        '!md:text-xxl !text-xl !font-bold dark:text-darkFullLight'
                      }
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      fontFamily={'inherit'}
                      className={
                        '!text-base	 text-primaryMiddle opacity-80 dark:text-darkFullLight'
                      }
                      dangerouslySetInnerHTML={{
                        __html: item.answer,
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;

  const language = locale?.toUpperCase();
  const { data } = await client.query({
    query: FAQ_PAGE_QUERY,
    variables: {
      language,
    },
  });

  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });

  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
